import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Paper,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  AccessTime as TimeIcon,
  Notes as NotesIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  MedicalServices as MedicalIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchAppointmentById, cancelAppointment } from '../../store/slices/appointmentSlice';
import { showSuccess, showError } from '../../store/slices/uiSlice';

/**
 * Appointment Details Page
 * Premium appointment details view for Civilhealth Healthcare System.
 *
 * @author Civilhealth Development Team
 */

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'CONFIRMED':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'COMPLETED':
      return 'info';
    case 'CANCELLED':
      return 'error';
    case 'IN_PROGRESS':
      return 'info';
    default:
      return 'default';
  }
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'Pending';
    case 'CONFIRMED':
      return 'Confirmed';
    case 'IN_PROGRESS':
      return 'In Progress';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'NO_SHOW':
      return 'No Show';
    default:
      return status;
  }
};

const getAppointmentTypeLabel = (type: string): string => {
  switch (type) {
    case 'HOME_VISIT':
      return 'Home Visit';
    case 'VIDEO_CONSULTATION':
      return 'Video Consultation';
    case 'TELEPHONE_CONSULTATION':
      return 'Telephone Consultation';
    case 'EMERGENCY_VISIT':
      return 'Emergency Visit';
    case 'FOLLOW_UP':
      return 'Follow-up';
    default:
      return type;
  }
};

const AppointmentDetailsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentAppointment: appointment, loading, error } = useSelector(
    (state: RootState) => state.appointment
  );

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');

  useEffect(() => {
    if (id) {
      dispatch(fetchAppointmentById(Number(id)) as any);
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (error) {
      dispatch(showError(error));
    }
  }, [error, dispatch]);

  const handleCancelAppointment = async () => {
    if (!cancellationReason.trim()) {
      dispatch(showError('Please provide a cancellation reason'));
      return;
    }

    try {
      await dispatch(
        cancelAppointment({
          id: Number(id),
          reason: cancellationReason,
          cancelledBy: user?.username || '',
        }) as any
      );
      dispatch(showSuccess('Appointment cancelled successfully'));
      setCancelDialogOpen(false);
      navigate('/appointments');
    } catch (err) {
      dispatch(showError('Failed to cancel appointment'));
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!appointment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Appointment not found</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/appointments')} sx={{ mt: 2 }}>
          Back to Appointments
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/appointments')}>
            Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Appointment Details
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Status Banner */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2,
          background: appointment.status === 'CANCELLED'
            ? 'linear-gradient(135deg, #fee2e2 0%, #fef2f2 100%)'
            : appointment.status === 'CONFIRMED'
            ? 'linear-gradient(135deg, #dcfce7 0%, #f0fdf4 100%)'
            : 'linear-gradient(135deg, #fef3c7 0%, #fffbeb 100%)',
        }}
      >
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CalendarIcon sx={{ fontSize: 40, color: appointment.status === 'CANCELLED' ? 'error.main' : 'success.main' }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  Booking #{appointment.bookingNumber || `APT-${appointment.id}`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDate(appointment.appointmentDate)} at {formatTime(appointment.startTime || appointment.appointmentTime)}
                </Typography>
              </Box>
            </Box>
            <Chip
              label={getStatusLabel(appointment.status)}
              color={getStatusColor(appointment.status)}
              size="medium"
              sx={{ fontSize: '1rem', py: 2, px: 1 }}
            />
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Doctor Information */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title="Doctor Information"
              avatar={<MedicalIcon />}
              sx={{ borderBottom: '1px solid #e0e0e0' }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'transparent' , fontSize: '1.5rem' }}>
                  {(appointment.doctorName?.charAt(0) || 'D')}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {appointment.doctorName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {appointment.doctorSpecialization}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      <PhoneIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                      {appointment.doctorMobile || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Patient Information */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title="Patient Information"
              avatar={<PersonIcon />}
              sx={{ borderBottom: '1px solid #e0e0e0' }}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Patient Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {appointment.patientName || appointment.officerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Contact
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {appointment.patientMobile || appointment.officerMobile}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {appointment.patientEmail || appointment.officerEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Appointment Type
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {getAppointmentTypeLabel(appointment.appointmentType)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Medical Details */}
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardHeader
              title="Medical Details"
              avatar={<NotesIcon />}
              sx={{ borderBottom: '1px solid #e0e0e0' }}
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Symptoms
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'transparent' }}>
                    <Typography variant="body1">
                      {appointment.symptoms || 'No symptoms described'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Notes
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'transparent' }}>
                    <Typography variant="body1">
                      {appointment.notes || 'No additional notes'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Appointment Summary */}
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', position: 'sticky', top: 20 }}>
            <CardHeader title="Appointment Summary" />
            <Divider />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatDate(appointment.appointmentDate)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {formatTime(appointment.startTime || appointment.appointmentTime)}
                      {appointment.endTime && ` - ${formatTime(appointment.endTime)}`}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {getAppointmentTypeLabel(appointment.appointmentType)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={getStatusLabel(appointment.status)}
                      size="small"
                      color={getStatusColor(appointment.status)}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Actions */}
              <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {(appointment.status === 'PENDING' || appointment.status === 'CONFIRMED') && (
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<CancelIcon />}
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    Cancel Appointment
                  </Button>
                )}
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/appointments')}
                >
                  Back to List
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancel Appointment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this appointment? This action cannot be undone.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Cancellation Reason"
            fullWidth
            multiline
            rows={3}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>No, Keep it</Button>
          <Button onClick={handleCancelAppointment} color="error" variant="contained">
            Yes, Cancel Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentDetailsPage;